
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { isVendedorScopedRole } from "@/types/auth";

export interface Cliente {
  id: number;
  cliente_id?: string;
  nome: string;
  email?: string;
  telefone?: string;
  cpf_cnpj?: string;
  endereco?: string;
  numero?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  vendedor_id?: string | null;
  created_at: string;
  updated_at: string;
}

export type ClientesFiltroParams = {
  periodo?: { dataInicio?: string; dataFim?: string } | null; // clientes.created_at
  criadoPorId?: string | null; // clientes.vendedor_id
  estado?: string | null; // clientes.estado (UF)
  cidade?: string | null; // clientes.cidade
} | null;

// Hook para contar total de clientes
export function useClientesCount() {
  const { user, profile, loading } = useAuth();
  const isVendedor = isVendedorScopedRole(profile?.role) && !!user?.id;

  return useQuery({
    queryKey: ['clientes', 'count', isVendedor ? user?.id : null],
    queryFn: async () => {
      let query = supabase.from('clientes').select('*', { count: 'exact', head: true });
      if (isVendedor) query = query.eq('vendedor_id', user!.id);
      const { count, error } = await query;

      if (error) {
        console.error('❌ Erro ao contar clientes:', error);
        throw error;
      }

      return count || 0;
    },
    enabled: !loading,
    staleTime: 60 * 1000, // 1 minuto - contagem muda menos frequentemente
    refetchOnWindowFocus: false,
  });
}

// Hook para buscar clientes paginados
export function useClientesPaginated(
  page: number = 1,
  limit: number = 15,
  searchTerm?: string,
  filtros?: ClientesFiltroParams,
  sort?: { field: keyof Cliente | 'id'; direction: 'asc' | 'desc' } | null,
  options?: { enabled?: boolean }
) {
  const { user, profile, loading } = useAuth();
  const isVendedor = isVendedorScopedRole(profile?.role) && !!user?.id;

  return useQuery({
    queryKey: [
      'clientes',
      'paginated',
      page,
      limit,
      searchTerm,
      filtros ?? null,
      sort?.field ?? 'id',
      sort?.direction ?? 'desc',
      isVendedor ? user?.id : null
    ],
    queryFn: async () => {
      console.log(`🔍 Buscando clientes - Página ${page}, Limite ${limit}, Busca: ${searchTerm || 'nenhuma'}...`);
      
      try {
        const from = (page - 1) * limit;
        const to = from + limit - 1;

        // Construir query base
        let query = supabase
          .from('clientes')
          .select('*', { count: 'exact' });

        if (isVendedor) query = query.eq('vendedor_id', user!.id);

        // Filtro por período (created_at)
        const periodo = filtros && "periodo" in filtros ? filtros.periodo : null;
        if (periodo?.dataInicio) {
          query = query.gte('created_at', `${periodo.dataInicio}T00:00:00`);
        }
        if (periodo?.dataFim) {
          query = query.lte('created_at', `${periodo.dataFim}T23:59:59`);
        }

        const criadoPorId = (filtros && "criadoPorId" in filtros ? (filtros.criadoPorId ?? "") : "").trim();
        if (criadoPorId) {
          query = query.eq("vendedor_id", criadoPorId);
        }
        const estado = (filtros && "estado" in filtros ? (filtros.estado ?? "") : "").trim();
        if (estado) {
          query = query.eq("estado", estado.toUpperCase());
        }
        const cidade = (filtros && "cidade" in filtros ? (filtros.cidade ?? "") : "").trim();
        if (cidade) {
          query = query.eq("cidade", cidade);
        }

        // Aplicar busca se fornecido
        if (searchTerm && searchTerm.trim()) {
          const searchTermTrimmed = searchTerm.trim();
          const searchLower = searchTermTrimmed.toLowerCase();
          
          // Remover caracteres não numéricos para busca de CPF/CNPJ e telefone
          const searchNumbersOnly = searchTermTrimmed.replace(/\D/g, '');
          
          // Se for número puro (apenas dígitos)
          if (searchNumbersOnly.length > 0 && /^\d+$/.test(searchTermTrimmed)) {
            // Se tiver até 6 dígitos, pode ser um ID - buscar por ID e CPF/CNPJ
            // Se tiver mais de 6 dígitos, provavelmente é CPF/CNPJ - buscar apenas em CPF/CNPJ
            if (searchNumbersOnly.length <= 6) {
              // Número pequeno: pode ser ID ou parte de CPF/CNPJ
              const idSearch = parseInt(searchTermTrimmed);
              // Buscar por ID exato OU CPF/CNPJ que contenha os números
              query = query.or(`id.eq.${idSearch},cpf_cnpj.ilike.%${searchNumbersOnly}%`);
            } else {
              // Número grande (CPF/CNPJ): buscar apenas em CPF/CNPJ, não tentar converter para ID
              query = query.ilike('cpf_cnpj', `%${searchNumbersOnly}%`);
            }
          } else {
            // Aspas no padrão ilike evitam quebra do .or() quando o termo tem espaços (ex.: "CANAA VEICULOS")
            const escaped = searchTermTrimmed.replace(/"/g, '""');
            const pattern = `"%${escaped}%"`;
            query = query.or(
              `nome.ilike.${pattern},cpf_cnpj.ilike.${pattern},email.ilike.${pattern},telefone.ilike.${pattern}`
            );
          }
        }

        // Ordenar e paginar
        const sortField = (sort?.field ?? 'id') as string;
        const sortDirection = sort?.direction ?? 'desc';
        query = query.order(sortField, { ascending: sortDirection === 'asc' }).range(from, to);

        const { data, error, count } = await query;

        if (error) {
          console.error('❌ Erro ao buscar clientes:', error);
          throw error;
        }

        console.log(`✅ Clientes encontrados na página ${page}:`, data?.length || 0, `Total: ${count || 0}`);
        return { data: (data || []) as Cliente[], count: count || 0 };
      } catch (error) {
        console.error('💥 Erro geral na busca de clientes:', error);
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
    refetchOnWindowFocus: false, // Desabilitado para reduzir egresso
    gcTime: 10 * 60 * 1000, // 10 minutos de cache
    retry: 1, // Reduzir tentativas
    retryDelay: 500,
    enabled: (options?.enabled ?? true) && !loading,
  });
}

// Hook legado - mantém contrato antigo retornando apenas os clientes
export function useClientes(limit: number = 200) {
  const { user, profile, loading } = useAuth();
  const isVendedor = isVendedorScopedRole(profile?.role) && !!user?.id;

  return useQuery({
    queryKey: ['clientes', 'default', limit, isVendedor ? user?.id : null],
    queryFn: async () => {
      let query = supabase
        .from('clientes')
        .select('*')
        .order('id', { ascending: false })
        .limit(limit);
      if (isVendedor) query = query.eq('vendedor_id', user!.id);
      const { data, error } = await query;

      if (error) {
        console.error('❌ Erro ao buscar clientes (hook simples):', error);
        throw error;
      }

      return (data || []) as Cliente[];
    },
    enabled: !loading,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/** Busca um cliente pelo ID (útil quando o orçamento já tem cliente vinculado). */
export function useClienteById(clienteId?: number | null, enabled = true) {
  return useQuery({
    queryKey: ["cliente", clienteId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clientes")
        .select("*")
        .eq("id", clienteId!)
        .maybeSingle();
      if (error) throw error;
      return data as Cliente | null;
    },
    enabled: enabled && !!clienteId && clienteId > 0,
    staleTime: 60 * 1000,
  });
}
